import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.getMe()
        .then(response => {
          setUser(response.data.user);
        })
        .catch(error => {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setUser(user);
      toast.success('Login realizado com sucesso!');

      return { success: true, user };
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao fazer login');
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao fazer login'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      toast.success('Cadastro enviado. Aguarde aprovação.');
      return { success: true, data: response.data };
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao cadastrar');
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao cadastrar'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.info('Você saiu da sua conta.');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authService.updateProfile(profileData);
      setUser(response.data.user);
      toast.success('Perfil atualizado com sucesso!');
      return { success: true, user: response.data.user };
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar perfil');
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao atualizar perfil'
      };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      await authService.changePassword(passwordData);
      toast.success('Senha alterada com sucesso!');
      return { success: true };
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao alterar senha');
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao alterar senha'
      };
    }
  };

  const resetPassword = async (email) => {
    try {
      const response = await authService.resetPassword(email);
      toast.success(response.data?.message || 'Se existir conta, enviaremos uma nova senha por email.');
      return { success: true, message: response.data.message };
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao redefinir senha');
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao redefinir senha'
      };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };


  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    resetPassword,
    updateUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'superadmin',
    isAdmin: user?.role === 'superadmin' || user?.role === 'admin',
    isDJ: user?.role === 'dj',
    isOrganizer: user?.role === 'organizer'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
